import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';

import {
  apiGetFarmFieldWithLogs,
  apiGetFarmFieldsWithLogs,
  apiGetFarmNotes,
  apiGetSoilAssessments,
} from 'constants/Api';
import {IFarmFieldLogData} from 'constants/interfaces';
import {lshDownloadDataLogPdfReport} from './FieldDataLogPdfReport';

type TLogScope = 'farm' | 'field';
type TCategoryKey =
  | 'notes'
  | 'growthObservations'
  | 'issueLogs'
  | 'managementLogs'
  | 'harvestSummaries'
  | 'postHarvestReviews'
  | 'soilData'
  | 'customerSupport';
type TDataPointTypeFilter = 'all' | TCategoryKey;
type TDateRangePreset =
  | 'all'
  | 'today'
  | 'pastWeek'
  | 'pastTwoWeeks'
  | 'pastMonth'
  | 'pastQuarter'
  | 'pastSeason'
  | 'pastYear'
  | 'custom';

interface FieldDataLogDialogProps {
  open: boolean;
  scope: TLogScope;
  farmId?: number | string;
  fieldId?: number | string;
  fieldName?: string;
  farmName?: string;
  companyName?: string;
  farmerName?: string;
  province?: string;
  onClose: () => void;
}

interface ILogTimelineEntry {
  key: string;
  category: TCategoryKey;
  item: Record<string, unknown>;
  fieldLabel: string;
  cropLabel: string;
  heading: string;
  body: string;
  primaryDateLabel: string;
  images: ILogImage[];
  sortTimestamp: number;
}

interface ILogImage {
  url: string;
  caption: string;
}

const CATEGORY_CONFIG: Array<{key: TCategoryKey; label: string}> = [
  {key: 'notes', label: 'Notes'},
  {key: 'growthObservations', label: 'Growth observations'},
  {key: 'issueLogs', label: 'Issue logs'},
  {key: 'managementLogs', label: 'Management logs'},
  {key: 'harvestSummaries', label: 'Harvest summaries'},
  {key: 'postHarvestReviews', label: 'Post-harvest reviews'},
  {key: 'soilData', label: 'Soil data'},
  {key: 'customerSupport', label: 'Customer support'},
];

const CATEGORY_SINGULAR_LABELS: Record<TCategoryKey, string> = {
  notes: 'Note',
  growthObservations: 'Growth observation',
  issueLogs: 'Issue log',
  managementLogs: 'Management log',
  harvestSummaries: 'Harvest summary',
  postHarvestReviews: 'Post-harvest review',
  soilData: 'Soil assessment',
  customerSupport: 'Support request',
};

const DATA_POINT_TYPE_OPTIONS: Array<{
  value: TDataPointTypeFilter;
  label: string;
}> = [
  {value: 'all', label: 'All data point types'},
  ...CATEGORY_CONFIG.map(({key, label}) => ({
    value: key,
    label,
  })),
];

const DATE_RANGE_OPTIONS: Array<{
  value: TDateRangePreset;
  label: string;
}> = [
  {value: 'all', label: 'All dates'},
  {value: 'today', label: 'Today'},
  {value: 'pastWeek', label: 'Past week'},
  {value: 'pastTwoWeeks', label: 'Past 2 weeks'},
  {value: 'pastMonth', label: 'Past month'},
  {value: 'pastQuarter', label: 'Past quarter'},
  {value: 'pastSeason', label: 'Past season'},
  {value: 'pastYear', label: 'Past year'},
  {value: 'custom', label: 'Custom'},
];

const ROW_LABELS: Record<string, string> = {
  observationDate: 'Observation date',
  cropGrowthStage: 'Crop growth stage',
  averagePlantHeightCm: 'Average plant height (cm)',
  leafAreaIndex: 'Leaf area index',
  overallCropHealthRating: 'Overall crop health rating',
  pestAndDiseasePresence: 'Pest and disease presence',
  weedPressure: 'Weed pressure',
  floweringIntensity: 'Flowering intensity',
  fruitSetIntensity: 'Fruit set intensity',
  estimatedYieldKgha: 'Estimated yield (kg/ha)',
  germinationDate: 'Germination date',
  expectedGerminationRate: 'Expected germination rate',
  estimatedYield: 'Estimated yield',
  yieldEstimate: 'Yield estimate',
  plantHeightCm: 'Plant height (cm)',
  germinationRatePct: 'Germination rate %',
  seedlingVigorScale: 'Seedling vigor scale',
  rootLengthScale: 'Root length scale (cm)',
  cropMaturityAssessment: 'Crop maturity assessment',
  uniformityOfMaturityScale: 'Uniformity of maturity scale',
  leavesPerPlant: 'Leaves per plant',
  floweringUniformityScale: 'Flowering uniformity scale',
  fruitSetPercent: 'Fruit set %',
  fruitSetUniformityScale: 'Fruit set uniformity scale',
  fruitAvgLengthCm: 'Fruit avg length (cm)',
  pctCropReadyForHarvest: '% crop ready for harvest',
  issueDate: 'Issue date',
  issueType: 'Issue type',
  description: 'Description',
  severityScale: 'Severity scale',
  affectedAreaHectares: 'Affected area (ha)',
  controlMeasuresApplied: 'Control measures applied',
  preventativeMeasuresTaken: 'Preventative measures taken',
  curativeMeasuresTaken: 'Curative measures taken',
  issueStatus: 'Issue status',
  issueDuringHarvest: 'Issue during harvest',
  logDate: 'Log date',
  activityType: 'Activity type',
  inputsUsed: 'Inputs used',
  fertilizerType: 'Fertilizer type',
  applicationRateKgha: 'Application rate (kg/ha)',
  laborHours: 'Labor hours',
  machineryUsed: 'Machinery used',
  irrigationFrequency: 'Irrigation frequency',
  irrigationVolume: 'Irrigation volume',
  weatherConditions: 'Weather conditions',
  waterStressSigns: 'Water stress signs',
  initialTreatmentActions: 'Initial treatment actions',
  harvestDate: 'Harvest date',
  totalHarvestedQuantityKg: 'Total harvested quantity (kg)',
  avgYieldPerHectareKgha: 'Avg yield per hectare (kg/ha)',
  harvestMethod: 'Harvest method',
  pctFirstGradeProduce: 'First grade produce %',
  commonQualityIssues: 'Common quality issues',
  harvestEfficiencyScale: 'Harvest efficiency scale',
  qualityGradesDistribution: 'Quality grades distribution',
  laborersUsed: 'Laborers used',
  equipmentUsed: 'Equipment used',
  initialProcessingSteps: 'Initial processing steps',
  storageConditions: 'Storage conditions',
  transportationArrangements: 'Transportation arrangements',
  reviewDate: 'Review date',
  marketPricesAchieved: 'Market prices achieved',
  costOfProductionAnalysis: 'Cost of production analysis',
  profitabilityAssessment: 'Profitability assessment',
  laborEfficiencyChallenges: 'Labor efficiency challenges',
  equipmentPerformanceIssues: 'Equipment performance issues',
  recommendationsForNextSeason: 'Recommendations for next season',
  areasForImprovement: 'Areas for improvement',
  keySuccesses: 'Key successes',
  comparisonWithExpectedYields: 'Comparison with expected yields',
  pestDiseaseEffectiveness: 'Pest disease effectiveness',
  nutrientMgmtEffectiveness: 'Nutrient management effectiveness',
  adjustmentsMadeDuringSeason: 'Adjustments made during season',
  impactOnYieldQuality: 'Impact on yield quality',
  lessonsLearned: 'Lessons learned',
  noteDate: 'Note date',
  noteText: 'Note text',
  images: 'Images',
  soilType: 'Soil type',
  soilStructure: 'Soil structure',
  hardpanPresence: 'Hardpan presence',
  organicMatterPct: 'Organic matter %',
  pH: 'pH',
  P: 'Phosphorus (P)',
  K: 'Potassium (K)',
  Ca: 'Calcium (Ca)',
  Mg: 'Magnesium (Mg)',
  Na: 'Sodium (Na)',
  Zn: 'Zinc (Zn)',
  Mn: 'Manganese (Mn)',
  Cu: 'Copper (Cu)',
  exchangeableAcidity: 'Exchangeable acidity',
  totalCations: 'Total cations',
  anions: 'Anions',
  soilAmendmentsNeeded: 'Soil amendments needed',
  suggestedCropVarieties: 'Suggested crop varieties',
  pestWeedStrategies: 'Pest/weed strategies',
};

const META_KEYS = new Set([
  'id',
  '__typename',
  'title',
  'comment',
  'noteText',
  'farmFieldId',
  'noteId',
  'createdAt',
  'updatedAt',
]);

const DATE_KEYS = [
  'noteDate',
  'observationDate',
  'issueDate',
  'logDate',
  'harvestDate',
  'reviewDate',
  'germinationDate',
  'createdAt',
  'updatedAt',
];

const IMAGE_URL_REGEX = /https?:\/\/[^\s<>"'`]+/gi;
const IMAGE_FILE_EXTENSION_REGEX =
  /\.(png|jpe?g|gif|webp|bmp|heic|heif|svg)(\?.*)?$/i;

const FIELD_DIALOG_DESCRIPTION =
  'Showing all field notes and data points for this field in one date-ordered timeline.';

const FARM_DIALOG_DESCRIPTION =
  'Showing all field notes and data points across the farm in one date-ordered timeline.';

function hasValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

function formatDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseDateValue(value?: string | number | null): Date | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number') {
    const timestamp = value < 1e11 ? value * 1000 : value;
    const parsed = new Date(timestamp);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  if (/^\d+$/.test(trimmed)) {
    const numeric = Number(trimmed);
    if (!Number.isNaN(numeric)) {
      const timestamp = trimmed.length <= 10 ? numeric * 1000 : numeric;
      const parsed = new Date(timestamp);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed;
      }
    }
  }

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function normalizeDateInputValue(value?: string | number | null): string {
  const parsed = parseDateValue(value);
  return parsed ? formatDateInputValue(parsed) : '';
}

function formatDate(value?: string | number | null): string {
  if (value === null || value === undefined) return '';
  const parsed = parseDateValue(value);
  if (!parsed) return String(value);

  const rawValue = typeof value === 'string' ? value.trim() : String(value);
  const hasTime =
    typeof value === 'number' ||
    /^\d+$/.test(rawValue) ||
    rawValue.includes('T');

  return parsed.toLocaleString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(hasTime
      ? {
          hour: 'numeric',
          minute: '2-digit',
        }
      : {}),
  });
}

function formatLabel(key: string): string {
  return (
    ROW_LABELS[key] ??
    key
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/^./, (value) => value.toUpperCase())
  );
}

function formatValue(key: string, value: unknown): string {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (typeof value === 'number') {
    if (DATE_KEYS.includes(key)) {
      return formatDate(value);
    }
    return Number.isInteger(value) ? value.toString() : value.toFixed(2);
  }

  if (typeof value === 'string') {
    if (DATE_KEYS.includes(key)) {
      return formatDate(value);
    }
    return value;
  }

  return String(value);
}

function asDateInputValue(value: unknown): string | number | null | undefined {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    value === null ||
    value === undefined
  ) {
    return value;
  }

  return undefined;
}

function getFieldLabel(field: IFarmFieldLogData, fallbackFieldName?: string) {
  return (
    field.info?.fieldName || fallbackFieldName || `Field ${field.id || ''}`
  );
}

function getFieldCrop(field: IFarmFieldLogData) {
  return field.info?.produce?.name || '';
}

function getOwnedNoteIds(field: IFarmFieldLogData): Set<string> {
  const ownedIds = new Set<string>();
  const sources: Array<unknown> = [
    field.growthObservations,
    field.issueLogs,
    field.managementLogs,
    field.harvestSummaries,
    field.postHarvestReviews,
    field.soilAssessments,
  ];

  sources.forEach((source) => {
    if (!Array.isArray(source)) return;
    source.forEach((item) => {
      const record = toRecord(item);
      if (!record) return;
      const noteId = record.noteId;
      if (typeof noteId === 'string' || typeof noteId === 'number') {
        ownedIds.add(String(noteId));
      } else {
        const noteObj = toRecord(record.note);
        if (
          noteObj &&
          (typeof noteObj.id === 'string' || typeof noteObj.id === 'number')
        ) {
          ownedIds.add(String(noteObj.id));
        }
      }
    });
  });

  return ownedIds;
}

function getNoteHeadingText(item: unknown): string {
  const record = toRecord(item);
  if (!record) return '';

  const title = record.title;
  if (typeof title === 'string' && title.trim()) {
    return title.trim().toLowerCase();
  }

  const noteText = record.noteText;
  if (typeof noteText === 'string' && noteText.trim()) {
    return noteText.trim().split('\n')[0].toLowerCase();
  }

  const comment = record.comment;
  if (typeof comment === 'string' && comment.trim()) {
    return comment.trim().split('\n')[0].toLowerCase();
  }

  return '';
}

const COMPANION_NOTE_HEADING_PREFIXES = [
  'soil assessment',
  'issue log:',
  'growth observation:',
  'management log',
  'harvest summary',
  'post harvest review',
];

function isCompanionNoteHeading(heading: string): boolean {
  return COMPANION_NOTE_HEADING_PREFIXES.some((prefix) =>
    heading.startsWith(prefix),
  );
}

function getCategoryItems(field: IFarmFieldLogData, key: TCategoryKey) {
  if (key === 'soilData') {
    const soilAssessments = Array.isArray(field.soilAssessments)
      ? field.soilAssessments
      : [];
    return soilAssessments as Array<Record<string, unknown>>;
  }

  if (key === 'customerSupport') {
    const allNotes = Array.isArray(field.notes) ? field.notes : [];
    return allNotes.filter((note) =>
      getNoteHeadingText(note).includes('[support]'),
    );
  }

  if (key === 'notes') {
    const allNotes = Array.isArray(field.notes) ? field.notes : [];
    const ownedNoteIds = getOwnedNoteIds(field);

    return allNotes.filter((note) => {
      const noteId = (note as Record<string, unknown>)?.id;
      if (
        (typeof noteId === 'string' || typeof noteId === 'number') &&
        ownedNoteIds.has(String(noteId))
      ) {
        return false;
      }

      const heading = getNoteHeadingText(note);
      if (heading.includes('[support]')) return false;

      if (isCompanionNoteHeading(heading)) return false;

      return true;
    });
  }

  const value = field[key as keyof IFarmFieldLogData];
  return Array.isArray(value) ? value : [];
}

function toRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
}

function getImageUrl(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }

  const record = toRecord(value);
  if (!record) return '';

  const imageUrl = record.imageUrl;
  if (typeof imageUrl === 'string') return imageUrl.trim();

  const url = record.url;
  if (typeof url === 'string') return url.trim();

  const src = record.src;
  if (typeof src === 'string') return src.trim();

  return '';
}

function getImageCaption(value: unknown): string {
  const record = toRecord(value);
  if (!record) return '';

  const caption = record.caption;
  return typeof caption === 'string' ? caption.trim() : '';
}

function sanitizeUrl(url: string): string {
  return url.trim().replace(/[),.;!?]+$/g, '');
}

function isImageUrl(url: string): boolean {
  return IMAGE_FILE_EXTENSION_REGEX.test(url);
}

function getImageUrlsFromText(value: unknown): string[] {
  if (typeof value !== 'string') {
    return [];
  }

  const seen = new Set<string>();
  const matches = value.match(IMAGE_URL_REGEX) || [];

  return matches
    .map((match) => sanitizeUrl(match))
    .filter((url) => {
      if (!url || !isImageUrl(url) || seen.has(url)) {
        return false;
      }
      seen.add(url);
      return true;
    });
}

function getImagesFromTextValue(value: unknown): ILogImage[] {
  return getImageUrlsFromText(value).map((url) => ({
    url,
    caption: '',
  }));
}

function mergeImages(...groups: ILogImage[][]): ILogImage[] {
  const merged: ILogImage[] = [];
  const seen = new Set<string>();

  groups.forEach((group) => {
    group.forEach((image) => {
      if (!image.url || seen.has(image.url)) {
        return;
      }
      seen.add(image.url);
      merged.push(image);
    });
  });

  return merged;
}

function getImagesFromValue(value: unknown): ILogImage[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const seen = new Set<string>();

  return value
    .map((item) => ({
      url: getImageUrl(item),
      caption: getImageCaption(item),
    }))
    .filter((item) => {
      if (!item.url || seen.has(item.url)) {
        return false;
      }
      seen.add(item.url);
      return true;
    });
}

function getImagesForItem(
  field: IFarmFieldLogData,
  item: Record<string, unknown>,
): ILogImage[] {
  const directImages = getImagesFromValue(item.images);
  const inlineImages = mergeImages(
    getImagesFromTextValue(item.comment),
    getImagesFromTextValue(item.noteText),
    getImagesFromTextValue(item.description),
  );
  let images = mergeImages(directImages, inlineImages);

  const noteIdValue = item.noteId || toRecord(item.note)?.id;
  const noteId =
    typeof noteIdValue === 'string' || typeof noteIdValue === 'number'
      ? String(noteIdValue)
      : '';

  if (!noteId) {
    return images;
  }

  const noteMatch = (field.notes || []).find(
    (note) => String(note.id) === noteId,
  );
  if (!noteMatch) {
    return images;
  }

  images = mergeImages(
    images,
    getImagesFromValue(noteMatch.images),
    getImagesFromTextValue(noteMatch.comment),
    getImagesFromTextValue(noteMatch.noteText),
  );

  return images;
}

async function attachLegacyNoteImages(
  fields: IFarmFieldLogData[],
): Promise<IFarmFieldLogData[]> {
  const fieldsWithNotes = fields.filter(
    (field) => Boolean(field.id) && Boolean(field.notes?.length),
  );
  if (!fieldsWithNotes.length) {
    return fields;
  }

  const noteImageMaps = await Promise.all(
    fieldsWithNotes.map(async (field) => {
      const fieldId = field.id as string | number;
      try {
        const legacyNotes = await apiGetFarmNotes(fieldId);
        const imageMap = new Map<string, string>();

        (Array.isArray(legacyNotes) ? legacyNotes : []).forEach(
          (legacyNote) => {
            const noteRecord = toRecord(legacyNote);
            if (!noteRecord) return;

            const noteId = noteRecord.id;
            const noteImageUrl = getImageUrl(noteRecord.imageUrl);
            if (
              (typeof noteId === 'string' || typeof noteId === 'number') &&
              noteImageUrl
            ) {
              imageMap.set(String(noteId), noteImageUrl);
            }
          },
        );

        return {fieldId: String(fieldId), imageMap};
      } catch (legacyError) {
        console.error('Failed to load legacy note images', legacyError);
        return {fieldId: String(fieldId), imageMap: new Map<string, string>()};
      }
    }),
  );

  const fieldImageMap = new Map(
    noteImageMaps.map((item) => [item.fieldId, item.imageMap]),
  );

  return fields.map((field) => {
    const fieldMap = fieldImageMap.get(String(field.id || ''));
    if (!fieldMap || !field.notes?.length) {
      return field;
    }

    const mergedNotes = field.notes.map((note) => {
      const noteId =
        typeof note.id === 'string' || typeof note.id === 'number'
          ? String(note.id)
          : '';
      if (!noteId) {
        return note;
      }

      if (getImagesFromValue(note.images).length) {
        return note;
      }

      const legacyUrl = fieldMap.get(noteId);
      if (!legacyUrl) {
        return note;
      }

      return {
        ...note,
        images: [
          {
            id: `legacy-${noteId}`,
            imageUrl: legacyUrl,
            caption: '',
          },
        ],
      };
    });

    return {
      ...field,
      notes: mergedNotes,
    };
  });
}

async function attachSoilAssessments(
  fields: IFarmFieldLogData[],
): Promise<IFarmFieldLogData[]> {
  const fieldsWithIds = fields.filter((field) => Boolean(field.id));
  if (!fieldsWithIds.length) return fields;

  const soilAssessmentsByFieldId = new Map<string, any[]>();
  await Promise.all(
    fieldsWithIds.map(async (field) => {
      try {
        const list = await apiGetSoilAssessments(field.id as string | number);
        soilAssessmentsByFieldId.set(String(field.id), list || []);
      } catch (error) {
        console.error(
          'Failed to load soil assessments for field',
          field.id,
          error,
        );
      }
    }),
  );

  if (!soilAssessmentsByFieldId.size) return fields;

  return fields.map((field) => {
    const list = soilAssessmentsByFieldId.get(String(field.id || ''));
    if (!list) return field;
    return {
      ...field,
      soilAssessments: list,
    };
  });
}

function getCategoryLabel(key: TCategoryKey) {
  return CATEGORY_CONFIG.find((category) => category.key === key)?.label || key;
}

function getFieldLogCount(field: IFarmFieldLogData) {
  return CATEGORY_CONFIG.reduce(
    (total, category) => total + getCategoryItems(field, category.key).length,
    0,
  );
}

function getPrimaryDateKey(item: Record<string, unknown>) {
  for (const key of DATE_KEYS) {
    const value = asDateInputValue(item[key]);
    if (normalizeDateInputValue(value)) {
      return key;
    }
  }
  return '';
}

function getSortTimestamp(item: Record<string, unknown>) {
  const primaryKey = getPrimaryDateKey(item);
  const value = primaryKey ? asDateInputValue(item[primaryKey]) : undefined;
  const parsed = primaryKey ? parseDateValue(value) : null;
  return parsed ? parsed.getTime() : 0;
}

function getPrimaryDate(item: Record<string, unknown>) {
  const primaryKey = getPrimaryDateKey(item);
  return primaryKey ? formatValue(primaryKey, item[primaryKey]) : '';
}

function getPrimaryDateValue(item: Record<string, unknown>) {
  const primaryKey = getPrimaryDateKey(item);
  const value = primaryKey ? asDateInputValue(item[primaryKey]) : undefined;
  return primaryKey ? normalizeDateInputValue(value) : '';
}

function getNoteHeading(item: Record<string, unknown>) {
  const title = item.title;
  if (typeof title === 'string' && title.trim()) {
    return title.trim();
  }

  const noteText = item.noteText;
  if (typeof noteText === 'string' && noteText.trim()) {
    return noteText.trim().split('\n')[0];
  }

  const comment = item.comment;
  if (typeof comment === 'string' && comment.trim()) {
    return comment.trim().split('\n')[0];
  }

  return 'Note';
}

function getCardHeading(
  category: TCategoryKey,
  item: Record<string, unknown>,
  index: number,
) {
  if (category === 'notes') {
    return getNoteHeading(item);
  }

  const cropGrowthStage = item.cropGrowthStage;
  if (typeof cropGrowthStage === 'string' && cropGrowthStage.trim()) {
    return cropGrowthStage;
  }

  const issueType = item.issueType;
  if (typeof issueType === 'string' && issueType.trim()) {
    return issueType;
  }

  const activityType = item.activityType;
  if (typeof activityType === 'string' && activityType.trim()) {
    return activityType;
  }

  const harvestMethod = item.harvestMethod;
  if (typeof harvestMethod === 'string' && harvestMethod.trim()) {
    return harvestMethod;
  }

  const profitabilityAssessment = item.profitabilityAssessment;
  if (
    category === 'postHarvestReviews' &&
    typeof profitabilityAssessment === 'string' &&
    profitabilityAssessment.trim()
  ) {
    return profitabilityAssessment.trim();
  }

  return `${CATEGORY_SINGULAR_LABELS[category]} ${index + 1}`;
}

function getCardBody(
  category: TCategoryKey,
  item: Record<string, unknown>,
  field?: IFarmFieldLogData,
) {
  const removeInlineImageLinks = (value: string) =>
    value
      .split('\n')
      .filter((line) => {
        const trimmed = line.trim();
        if (!trimmed) {
          return true;
        }

        const imageLineMatch = trimmed.match(
          /^image\s+\d+\s*:\s*(https?:\/\/\S+)$/i,
        );
        if (imageLineMatch) {
          return !isImageUrl(sanitizeUrl(imageLineMatch[1]));
        }

        if (/^https?:\/\/\S+$/i.test(trimmed)) {
          return !isImageUrl(sanitizeUrl(trimmed));
        }

        return true;
      })
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

  if (category === 'notes') {
    const noteText = item.noteText;
    if (typeof noteText === 'string' && noteText.trim()) {
      return removeInlineImageLinks(noteText.trim());
    }
  }

  if (category === 'issueLogs') {
    const description = item.description;
    if (typeof description === 'string' && description.trim()) {
      return removeInlineImageLinks(description.trim());
    }
  }

  let comment =
    typeof item.comment === 'string'
      ? item.comment
      : (toRecord(item.note)?.comment as string);

  if (!comment && field) {
    const noteIdValue = item.noteId || toRecord(item.note)?.id;
    if (noteIdValue) {
      const noteMatch = (field.notes || []).find(
        (n: any) => String(n.id) === String(noteIdValue),
      );
      if (noteMatch) {
        comment =
          typeof noteMatch.comment === 'string'
            ? noteMatch.comment
            : (noteMatch.noteText as string);
      }
    }
  }

  if (typeof comment === 'string' && comment.trim()) {
    return removeInlineImageLinks(comment.trim());
  }

  return '';
}

function getRows(item: Record<string, unknown>, category: TCategoryKey) {
  const primaryDateKey = getPrimaryDateKey(item);

  return Object.entries(item).filter(([key, value]) => {
    if (META_KEYS.has(key)) return false;
    if (key === primaryDateKey) return false;
    if (!hasValue(value)) return false;
    if (Array.isArray(value)) return false;
    if (typeof value === 'object') return false;
    if (category === 'notes' && key === 'noteText') return false;
    if (category === 'issueLogs' && key === 'description') return false;
    return true;
  });
}

function getItemSearchParts(
  field: IFarmFieldLogData,
  category: TCategoryKey,
  item: Record<string, unknown>,
) {
  const parts = [
    getFieldLabel(field),
    getFieldCrop(field),
    getCategoryLabel(category),
    CATEGORY_SINGULAR_LABELS[category],
    getCardHeading(category, item, 0),
    getCardBody(category, item, field),
  ];

  getImagesForItem(field, item).forEach((image) => {
    parts.push(image.caption);
    parts.push(image.url);
  });

  Object.entries(item).forEach(([key, value]) => {
    if (!hasValue(value)) return;

    parts.push(formatLabel(key));

    if (Array.isArray(value)) {
      value.forEach((arrayValue) => {
        if (
          typeof arrayValue === 'string' ||
          typeof arrayValue === 'number' ||
          typeof arrayValue === 'boolean'
        ) {
          parts.push(String(arrayValue));
        }
      });
      return;
    }

    if (typeof value === 'object') return;
    parts.push(formatValue(key, value));
  });

  return parts
    .filter((value) => typeof value === 'string' && value.trim().length > 0)
    .join(' ')
    .toLowerCase();
}

function addDays(baseDate: Date, days: number) {
  const nextDate = new Date(baseDate);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
}

function addMonths(baseDate: Date, months: number) {
  const nextDate = new Date(baseDate);
  nextDate.setMonth(nextDate.getMonth() + months);
  return nextDate;
}

function getAppliedDateRange(
  preset: TDateRangePreset,
  customStartDate: string,
  customEndDate: string,
) {
  if (preset === 'all') {
    return {startDate: '', endDate: ''};
  }

  if (preset === 'custom') {
    if (customStartDate && customEndDate && customStartDate > customEndDate) {
      return {
        startDate: customEndDate,
        endDate: customStartDate,
      };
    }

    return {
      startDate: customStartDate,
      endDate: customEndDate,
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const endDate = formatDateInputValue(today);
  let startDate = endDate;

  switch (preset) {
    case 'today':
      startDate = endDate;
      break;
    case 'pastWeek':
      startDate = formatDateInputValue(addDays(today, -6));
      break;
    case 'pastTwoWeeks':
      startDate = formatDateInputValue(addDays(today, -13));
      break;
    case 'pastMonth':
      startDate = formatDateInputValue(addMonths(today, -1));
      break;
    case 'pastQuarter':
      startDate = formatDateInputValue(addMonths(today, -3));
      break;
    case 'pastSeason':
      startDate = formatDateInputValue(addMonths(today, -6));
      break;
    case 'pastYear':
      startDate = formatDateInputValue(addMonths(today, -12));
      break;
    default:
      break;
  }

  return {startDate, endDate};
}

function itemMatchesFilters(
  field: IFarmFieldLogData,
  category: TCategoryKey,
  item: Record<string, unknown>,
  searchQuery: string,
  startDate: string,
  endDate: string,
) {
  if (searchQuery.trim()) {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!getItemSearchParts(field, category, item).includes(normalizedQuery)) {
      return false;
    }
  }

  const itemDate = getPrimaryDateValue(item);
  if ((startDate || endDate) && !itemDate) {
    return false;
  }

  if (startDate && itemDate < startDate) {
    return false;
  }

  if (endDate && itemDate > endDate) {
    return false;
  }

  return true;
}

function buildTimelineEntries(
  fields: IFarmFieldLogData[],
  dataPointType: TDataPointTypeFilter,
  searchQuery: string,
  startDate: string,
  endDate: string,
  fallbackFieldName?: string,
): ILogTimelineEntry[] {
  const entries: ILogTimelineEntry[] = [];

  fields.forEach((field, fieldIndex) => {
    const fieldLabel = getFieldLabel(field, fallbackFieldName);
    const cropLabel = getFieldCrop(field);

    CATEGORY_CONFIG.forEach((category) => {
      if (dataPointType !== 'all' && dataPointType !== category.key) {
        return;
      }

      getCategoryItems(field, category.key).forEach((rawItem, itemIndex) => {
        const item = rawItem as Record<string, unknown>;

        if (
          !itemMatchesFilters(
            field,
            category.key,
            item,
            searchQuery,
            startDate,
            endDate,
          )
        ) {
          return;
        }

        entries.push({
          key: `${field.id || fieldIndex}-${category.key}-${
            item.id || itemIndex
          }`,
          category: category.key,
          item,
          fieldLabel,
          cropLabel,
          heading: getCardHeading(category.key, item, itemIndex),
          body: getCardBody(category.key, item, field),
          primaryDateLabel: getPrimaryDate(item),
          images: getImagesForItem(field, item),
          sortTimestamp: getSortTimestamp(item),
        });
      });
    });
  });

  return [...entries].sort((entryA, entryB) => {
    if (entryA.sortTimestamp && entryB.sortTimestamp) {
      return entryB.sortTimestamp - entryA.sortTimestamp;
    }

    if (entryA.sortTimestamp) return -1;
    if (entryB.sortTimestamp) return 1;
    return entryA.heading.localeCompare(entryB.heading);
  });
}

function getRowValueForReport(key: string, value: unknown): string {
  if (key === 'images' && Array.isArray(value)) {
    return value
      .map(
        (item, index) =>
          `Image ${index + 1}: ${
            getImageUrl(item) || `Attachment ${index + 1}`
          }`,
      )
      .join(' | ');
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (typeof value === 'number') {
    if (DATE_KEYS.includes(key)) {
      const normalizedDate = normalizeDateInputValue(value);
      return normalizedDate || value.toString();
    }
    return Number.isInteger(value) ? value.toString() : value.toFixed(2);
  }

  if (typeof value === 'string') {
    if (DATE_KEYS.includes(key)) {
      const normalizedDate = normalizeDateInputValue(value);
      return normalizedDate || value;
    }
    return value;
  }

  return String(value);
}

function getDateOnlyForReport(value: unknown): string {
  const dateValue = asDateInputValue(value);
  return normalizeDateInputValue(dateValue);
}

function getDateRangeReportLabel(
  datePreset: TDateRangePreset,
  datePresetLabel: string,
  startDate: string,
  endDate: string,
) {
  if (datePreset !== 'custom') {
    return datePresetLabel;
  }

  if (startDate && endDate) {
    return `${startDate} to ${endDate}`;
  }

  if (startDate) {
    return `From ${startDate}`;
  }

  if (endDate) {
    return `Until ${endDate}`;
  }

  return 'Custom';
}

function renderTimelineCard(
  entry: ILogTimelineEntry,
  showFieldContext: boolean,
  onImageClick: (images: ILogImage[], index: number) => void,
  onEdit?: (entry: ILogTimelineEntry) => void,
  onDelete?: (entry: ILogTimelineEntry) => void,
) {
  const rows = getRows(entry.item, entry.category);

  return (
    <Box
      key={entry.key}
      mb={2}
      p={2}
      borderRadius={14}
      bgcolor="#FFFFFF"
      border="1px solid #DEE7E5"
      style={{
        boxShadow: '0 2px 8px rgba(1, 51, 51, 0.04)',
      }}
    >
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        flexWrap="wrap"
        style={{gap: 12}}
      >
        <Box
          display="flex"
          alignItems="center"
          flexWrap="wrap"
          style={{gap: 8}}
        >
          <Chip
            size="small"
            label={getCategoryLabel(entry.category)}
            style={{
              backgroundColor: '#E9F5F0',
              color: '#0A605B',
              fontWeight: 700,
            }}
          />

          {showFieldContext ? (
            <Chip
              size="small"
              label={entry.fieldLabel}
              style={{
                backgroundColor: '#F4F7F6',
                color: '#013333',
                fontWeight: 600,
              }}
            />
          ) : null}

          {entry.cropLabel ? (
            <Chip
              size="small"
              label={entry.cropLabel}
              style={{
                backgroundColor: '#EDF8F4',
                color: '#0A605B',
                fontWeight: 600,
              }}
            />
          ) : null}
        </Box>

        <Box display="flex" alignItems="center" style={{gap: 4}}>
          {entry.primaryDateLabel ? (
            <Typography
              variant="caption"
              style={{color: '#4F6B68', fontWeight: 600, whiteSpace: 'nowrap'}}
            >
              {entry.primaryDateLabel}
            </Typography>
          ) : null}
          {onEdit ? (
            <IconButton
              size="small"
              onClick={() => onEdit(entry)}
              aria-label="Edit log entry"
              title="Edit"
              style={{color: '#0A605B'}}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          ) : null}
          {onDelete ? (
            <IconButton
              size="small"
              onClick={() => onDelete(entry)}
              aria-label="Delete log entry"
              title="Delete"
              style={{color: '#972830'}}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          ) : null}
        </Box>
      </Box>

      <Typography
        variant="subtitle1"
        style={{fontWeight: 700, color: '#013333', marginTop: 12}}
      >
        {entry.heading}
      </Typography>

      {entry.body ? (
        <Typography
          variant="body2"
          style={{
            color: '#2A4D49',
            marginTop: 8,
            whiteSpace: 'pre-wrap',
            lineHeight: 1.55,
          }}
        >
          {entry.body}
        </Typography>
      ) : null}

      {entry.images.length ? (
        <Box mt={1.5}>
          <Typography
            variant="body2"
            style={{color: '#6B6B6B', marginBottom: 8}}
          >
            Images
          </Typography>
          <Box display="flex" flexWrap="wrap" style={{gap: 8}}>
            {entry.images.map((image, index) => (
              <button
                key={`${entry.key}-image-${index}`}
                type="button"
                onClick={() => onImageClick(entry.images, index)}
                title={image.caption || `Image ${index + 1}`}
                style={{
                  textDecoration: 'none',
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                }}
                aria-label={`Open image ${index + 1}`}
              >
                <Box
                  borderRadius={10}
                  border="1px solid #D9E6E3"
                  style={{
                    width: 76,
                    height: 76,
                    overflow: 'hidden',
                    backgroundColor: '#EEF3F1',
                  }}
                >
                  <img
                    src={image.url}
                    alt={image.caption || `Data log image ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                </Box>
              </button>
            ))}
          </Box>
        </Box>
      ) : null}

      {rows.length ? (
        <Box
          display="grid"
          mt={entry.body || entry.images.length ? 1.5 : 1.25}
          style={{gap: 10, gridTemplateColumns: 'minmax(130px, 170px) 1fr'}}
        >
          {rows.map(([key, value]) => (
            <React.Fragment key={`${entry.key}-${key}`}>
              <Typography variant="body2" style={{color: '#6B6B6B'}}>
                {formatLabel(key)}
              </Typography>
              <Typography
                variant="body2"
                style={{color: '#013333', whiteSpace: 'pre-wrap'}}
              >
                {formatValue(key, value)}
              </Typography>
            </React.Fragment>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

export const FieldDataLogDialog: React.FC<FieldDataLogDialogProps> = ({
  open,
  scope,
  farmId,
  fieldId,
  fieldName,
  farmName,
  companyName,
  farmerName,
  province,
  onClose,
}) => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const [fields, setFields] = React.useState<IFarmFieldLogData[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isExportingReport, setIsExportingReport] = React.useState(false);
  const [error, setError] = React.useState('');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [dataPointType, setDataPointType] =
    React.useState<TDataPointTypeFilter>('all');
  const [datePreset, setDatePreset] = React.useState<TDateRangePreset>('all');
  const [customStartDate, setCustomStartDate] = React.useState('');
  const [customEndDate, setCustomEndDate] = React.useState('');
  const [viewerImages, setViewerImages] = React.useState<ILogImage[]>([]);
  const [viewerIndex, setViewerIndex] = React.useState(0);

  React.useEffect(() => {
    if (!open) return;

    let isMounted = true;

    const loadLogs = async () => {
      if (scope === 'field' && !fieldId) {
        setError(
          'Field data log could not be loaded because the field id is missing.',
        );
        setFields([]);
        return;
      }

      if (scope === 'farm' && !farmId) {
        setError(
          'Farm data logs could not be loaded because the farm id is missing.',
        );
        setFields([]);
        return;
      }

      setIsLoading(true);
      setError('');

      try {
        const response =
          scope === 'field'
            ? await apiGetFarmFieldWithLogs(fieldId as string | number)
            : await apiGetFarmFieldsWithLogs(farmId as string | number);

        if (!isMounted) return;

        const nextFields = Array.isArray(response)
          ? response
          : response
          ? [response]
          : [];

        const fieldsWithLegacyImages = await attachLegacyNoteImages(nextFields);
        if (!isMounted) return;

        const fieldsWithSoil = await attachSoilAssessments(
          fieldsWithLegacyImages,
        );
        if (!isMounted) return;

        setFields(fieldsWithSoil);
      } catch (loadError) {
        console.error('Failed to load farm field logs', loadError);
        if (isMounted) {
          setError('Failed to load data logs.');
          setFields([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadLogs();

    return () => {
      isMounted = false;
    };
  }, [open, scope, farmId, fieldId]);

  React.useEffect(() => {
    if (open) return;
    setViewerImages([]);
    setViewerIndex(0);
  }, [open]);

  React.useEffect(() => {
    if (!open) return;

    setSearchQuery('');
    setDataPointType('all');
    setDatePreset('all');
    setCustomStartDate('');
    setCustomEndDate('');
  }, [open, scope, farmId, fieldId]);

  const {startDate, endDate} = React.useMemo(
    () => getAppliedDateRange(datePreset, customStartDate, customEndDate),
    [datePreset, customStartDate, customEndDate],
  );

  const timelineEntries = React.useMemo(
    () =>
      buildTimelineEntries(
        fields,
        dataPointType,
        searchQuery,
        startDate,
        endDate,
        scope === 'field' ? fieldName : undefined,
      ),
    [fields, dataPointType, searchQuery, startDate, endDate, scope, fieldName],
  );

  const dialogTitle = scope === 'field' ? 'Data log' : 'All field data logs';
  const dialogDescription =
    scope === 'field' ? FIELD_DIALOG_DESCRIPTION : FARM_DIALOG_DESCRIPTION;
  const totalLogCount = fields.reduce(
    (total, field) => total + getFieldLogCount(field),
    0,
  );
  const matchingLogCount = timelineEntries.length;
  const matchingFieldCount = new Set(
    timelineEntries.map((entry) => entry.fieldLabel),
  ).size;
  const hasDateFilter =
    datePreset !== 'all' &&
    (datePreset !== 'custom' || Boolean(startDate || endDate));
  const hasActiveFilters = Boolean(
    searchQuery.trim() || dataPointType !== 'all' || hasDateFilter,
  );
  const emptyResultMessage =
    hasActiveFilters && totalLogCount > 0
      ? 'No data logs match the current filters.'
      : 'No data logs found.';
  const datePresetLabel =
    DATE_RANGE_OPTIONS.find((option) => option.value === datePreset)?.label ||
    'All dates';
  const summaryLabel =
    scope === 'field'
      ? `${matchingLogCount} matching ${
          matchingLogCount === 1 ? 'log' : 'logs'
        }`
      : `${matchingLogCount} matching ${
          matchingLogCount === 1 ? 'log' : 'logs'
        } across ${matchingFieldCount} ${
          matchingFieldCount === 1 ? 'field' : 'fields'
        }`;
  const fieldSummary = fields[0];
  const dataPointTypeLabel =
    DATA_POINT_TYPE_OPTIONS.find((option) => option.value === dataPointType)
      ?.label || 'All data point types';
  const dateRangeReportLabel = getDateRangeReportLabel(
    datePreset,
    datePresetLabel,
    startDate,
    endDate,
  );

  const handleClearFilters = () => {
    setSearchQuery('');
    setDataPointType('all');
    setDatePreset('all');
    setCustomStartDate('');
    setCustomEndDate('');
  };

  const handleOpenImageViewer = React.useCallback(
    (images: ILogImage[], index: number) => {
      if (!images.length) return;
      const safeIndex = Math.max(0, Math.min(index, images.length - 1));
      setViewerImages(images);
      setViewerIndex(safeIndex);
    },
    [],
  );

  const handleCloseImageViewer = React.useCallback(() => {
    setViewerImages([]);
    setViewerIndex(0);
  }, []);

  const handlePreviousImage = React.useCallback(() => {
    setViewerIndex((current) => {
      if (!viewerImages.length) return 0;
      return current === 0 ? viewerImages.length - 1 : current - 1;
    });
  }, [viewerImages.length]);

  const handleNextImage = React.useCallback(() => {
    setViewerIndex((current) => {
      if (!viewerImages.length) return 0;
      return current === viewerImages.length - 1 ? 0 : current + 1;
    });
  }, [viewerImages.length]);

  const activeViewerImage = viewerImages[viewerIndex];

  const handleDownloadReport = async () => {
    try {
      setIsExportingReport(true);
      const now = new Date();
      const reportDate = formatDateInputValue(now);
      const reportCompanyName =
        companyName?.trim() || farmName?.trim() || 'Farm';
      const reportEntries = timelineEntries.map((entry) => ({
        categoryLabel: getCategoryLabel(entry.category),
        fieldLabel: entry.fieldLabel,
        cropLabel: entry.cropLabel,
        heading: entry.heading,
        body: entry.body,
        primaryDateLabel:
          getPrimaryDateValue(entry.item) ||
          getDateOnlyForReport(entry.primaryDateLabel) ||
          entry.primaryDateLabel,
        rows: [
          ...getRows(entry.item, entry.category).map(([key, value]) => ({
            label: formatLabel(key),
            value: getRowValueForReport(key, value),
          })),
        ],
        images: entry.images,
      }));

      await lshDownloadDataLogPdfReport({
        title: `${reportCompanyName} Report - ${reportDate}`,
        subtitle:
          scope === 'field'
            ? `Field report for ${fieldName || 'selected field'}`
            : 'Farm-wide report for filtered data logs',
        generatedAt: reportDate,
        summary: summaryLabel,
        filters: {
          search: searchQuery.trim(),
          dataPointType: dataPointTypeLabel,
          dateRange: dateRangeReportLabel,
        },
        context: {
          farmName: farmName?.trim() || 'Not specified',
          farmerName: farmerName?.trim() || 'Not specified',
          province: province?.trim() || 'Not specified',
        },
        entries: reportEntries,
      });
    } catch (downloadError) {
      console.error('Failed to export data log report', downloadError);
      window.alert('Failed to export report. Please try again.');
    } finally {
      setIsExportingReport(false);
    }
  };

  const handleEditLogEntry = React.useCallback((entry: ILogTimelineEntry) => {
    // Open edit modal or dialog for the selected entry
    // For now, log to console - you can implement the actual edit logic
    console.log('Edit log entry:', entry);
    window.alert(`Edit functionality coming soon for: ${entry.heading}`);
  }, []);

  const handleDeleteLogEntry = React.useCallback(
    async (entry: ILogTimelineEntry) => {
      // Confirm deletion with user
      if (
        !window.confirm(
          `Are you sure you want to delete this log entry?\n\n"${entry.heading}"`,
        )
      ) {
        return;
      }

      try {
        // TODO: Implement actual delete API call here
        // For now, just show a placeholder alert
        console.log('Delete log entry:', entry);
        window.alert('Delete functionality coming soon');
        // After successful deletion, you would refresh the logs:
        // await loadLogs();
      } catch (deleteError) {
        console.error('Failed to delete log entry', deleteError);
        window.alert('Failed to delete log entry. Please try again.');
      }
    },
    [],
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        fullScreen={isMobile}
        PaperProps={{
          style: isMobile
            ? {
                borderRadius: 0,
              }
            : {
                position: 'fixed',
                right: 16,
                top: 16,
                margin: 0,
                width: 620,
                maxWidth: 'calc(100vw - 32px)',
                height: 'calc(100vh - 32px)',
                borderRadius: 18,
                overflow: 'hidden',
              },
        }}
      >
        <DialogTitle
          disableTypography
          style={{
            padding: '20px 22px 14px',
            borderBottom: '1px solid #DEE7E5',
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Typography
                variant="h6"
                style={{fontWeight: 700, color: '#013333'}}
              >
                {dialogTitle}
              </Typography>
              <Typography
                variant="body2"
                style={{color: '#5A6B68', marginTop: 4}}
              >
                {scope === 'field' && fieldName
                  ? `${dialogDescription} (${fieldName})`
                  : dialogDescription}
              </Typography>
            </Box>

            <IconButton onClick={onClose} aria-label="Close data log dialog">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          style={{
            padding: 18,
            backgroundColor: '#F5F8F7',
            overflowY: 'auto',
          }}
        >
          <Box
            mb={2}
            p={2}
            borderRadius={14}
            style={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #DEE7E5',
            }}
          >
            <Box
              display="grid"
              style={{
                gap: 12,
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              }}
            >
              <TextField
                label="Search logs"
                placeholder="Note, type, field, or crop"
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{color: '#6D8C88'}} />
                    </InputAdornment>
                  ),
                  style: {borderRadius: 12, backgroundColor: '#FFFFFF'},
                }}
              />

              <TextField
                select
                label="Data point type"
                variant="outlined"
                size="small"
                value={dataPointType}
                onChange={(event) =>
                  setDataPointType(event.target.value as TDataPointTypeFilter)
                }
                InputProps={{
                  style: {borderRadius: 12, backgroundColor: '#FFFFFF'},
                }}
              >
                {DATA_POINT_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Date range"
                variant="outlined"
                size="small"
                value={datePreset}
                onChange={(event) =>
                  setDatePreset(event.target.value as TDateRangePreset)
                }
                InputProps={{
                  style: {borderRadius: 12, backgroundColor: '#FFFFFF'},
                }}
              >
                {DATE_RANGE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Collapse in={datePreset === 'custom'} timeout={220}>
              <Box
                mt={1.5}
                display="grid"
                style={{
                  gap: 12,
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                }}
              >
                <TextField
                  label="From date"
                  type="date"
                  variant="outlined"
                  size="small"
                  value={customStartDate}
                  onChange={(event) => setCustomStartDate(event.target.value)}
                  InputLabelProps={{shrink: true}}
                  inputProps={{max: customEndDate || undefined}}
                  InputProps={{
                    style: {borderRadius: 12, backgroundColor: '#FFFFFF'},
                  }}
                />

                <TextField
                  label="To date"
                  type="date"
                  variant="outlined"
                  size="small"
                  value={customEndDate}
                  onChange={(event) => setCustomEndDate(event.target.value)}
                  InputLabelProps={{shrink: true}}
                  inputProps={{min: customStartDate || undefined}}
                  InputProps={{
                    style: {borderRadius: 12, backgroundColor: '#FFFFFF'},
                  }}
                />
              </Box>
            </Collapse>

            <Box
              mt={1.5}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              style={{gap: 12}}
            >
              <Box
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                style={{gap: 8}}
              >
                <Typography variant="body2" style={{color: '#4F6663'}}>
                  {summaryLabel}
                </Typography>
                <Chip
                  size="small"
                  label="Newest first"
                  style={{
                    backgroundColor: '#EFF3F2',
                    color: '#013333',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  size="small"
                  label={datePresetLabel}
                  style={{
                    backgroundColor: '#EFF3F2',
                    color: '#013333',
                    fontWeight: 600,
                  }}
                />
              </Box>

              <Box display="flex" alignItems="center" style={{gap: 8}}>
                <Button
                  onClick={handleDownloadReport}
                  disabled={
                    isLoading || isExportingReport || !timelineEntries.length
                  }
                  style={{
                    borderRadius: 999,
                    color: '#FFFFFF',
                    backgroundColor: '#0A605B',
                    fontWeight: 700,
                    paddingInline: 18,
                  }}
                >
                  {isExportingReport ? 'Preparing PDF...' : 'Download report'}
                </Button>

                <Button
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters}
                  style={{
                    borderRadius: 999,
                    color: '#013333',
                    fontWeight: 700,
                    paddingInline: 18,
                  }}
                >
                  Clear filters
                </Button>
              </Box>
            </Box>
          </Box>

          {scope === 'field' && fieldSummary ? (
            <Box
              mb={2}
              p={2}
              borderRadius={14}
              style={{
                backgroundColor: '#FFFFFF',
                border: '1px solid #DEE7E5',
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                style={{gap: 12}}
              >
                <Box>
                  <Typography
                    variant="h6"
                    style={{fontWeight: 700, color: '#013333'}}
                  >
                    {getFieldLabel(fieldSummary, fieldName)}
                  </Typography>
                  {getFieldCrop(fieldSummary) ? (
                    <Typography
                      variant="body2"
                      style={{color: '#5A6B68', marginTop: 4}}
                    >
                      {getFieldCrop(fieldSummary)}
                    </Typography>
                  ) : null}
                </Box>

                <Chip
                  label={`${matchingLogCount} ${
                    matchingLogCount === 1 ? 'log' : 'logs'
                  }`}
                  style={{
                    backgroundColor: '#EFF3F2',
                    color: '#013333',
                    fontWeight: 700,
                  }}
                />
              </Box>
            </Box>
          ) : null}

          {isLoading ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight={280}
              style={{gap: 16}}
            >
              <CircularProgress />
              <Typography variant="body2" style={{color: '#6B6B6B'}}>
                Loading data logs...
              </Typography>
            </Box>
          ) : error ? (
            <Typography variant="body2" style={{color: '#972830'}}>
              {error}
            </Typography>
          ) : !fields.length || !timelineEntries.length ? (
            <Box
              p={3}
              borderRadius={14}
              style={{
                backgroundColor: '#FFFFFF',
                border: '1px solid #DEE7E5',
              }}
            >
              <Typography
                variant="subtitle1"
                style={{fontWeight: 700, color: '#013333', marginBottom: 6}}
              >
                Nothing to show
              </Typography>
              <Typography variant="body2" style={{color: '#5A6B68'}}>
                {emptyResultMessage}
              </Typography>
            </Box>
          ) : (
            <Box>
              {timelineEntries.map((entry, index) => (
                <React.Fragment key={entry.key}>
                  {renderTimelineCard(
                    entry,
                    scope === 'farm',
                    handleOpenImageViewer,
                    handleEditLogEntry,
                    handleDeleteLogEntry,
                  )}
                  {index < timelineEntries.length - 1 ? (
                    <Divider
                      style={{
                        margin: '0 4px 12px',
                        backgroundColor: 'rgba(1, 51, 51, 0.08)',
                      }}
                    />
                  ) : null}
                </React.Fragment>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(activeViewerImage)}
        onClose={handleCloseImageViewer}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          style: {
            borderRadius: isMobile ? 0 : 16,
            overflow: 'hidden',
            backgroundColor: '#041816',
            maxWidth: isMobile ? undefined : 920,
          },
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          style={{
            padding: '10px 12px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
          }}
        >
          <Typography style={{color: '#EAF3F1', fontWeight: 600}}>
            {activeViewerImage?.caption || `Image ${viewerIndex + 1}`}
            {viewerImages.length > 1
              ? ` (${viewerIndex + 1} of ${viewerImages.length})`
              : ''}
          </Typography>
          <IconButton
            onClick={handleCloseImageViewer}
            aria-label="Close image viewer"
            style={{color: '#EAF3F1'}}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          style={{
            backgroundColor: '#041816',
            padding: isMobile ? 8 : 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            minHeight: isMobile ? '56vh' : '64vh',
          }}
        >
          {activeViewerImage ? (
            <>
              {viewerImages.length > 1 ? (
                <IconButton
                  onClick={handlePreviousImage}
                  aria-label="Previous image"
                  style={{
                    position: 'absolute',
                    left: isMobile ? 8 : 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(1, 51, 51, 0.58)',
                    color: '#FFFFFF',
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>
              ) : null}

              <img
                src={activeViewerImage.url}
                alt={
                  activeViewerImage.caption ||
                  `Data log image ${viewerIndex + 1}`
                }
                style={{
                  maxWidth: '100%',
                  maxHeight: isMobile ? '72vh' : '70vh',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                  borderRadius: 12,
                  backgroundColor: '#0A2A28',
                }}
              />

              {viewerImages.length > 1 ? (
                <IconButton
                  onClick={handleNextImage}
                  aria-label="Next image"
                  style={{
                    position: 'absolute',
                    right: isMobile ? 8 : 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(1, 51, 51, 0.58)',
                    color: '#FFFFFF',
                  }}
                >
                  <ChevronRightIcon />
                </IconButton>
              ) : null}
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
};
